import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { API, formatApiError, formatCurrency, formatDateTime } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { StatusBadge } from "@/components/StatusBadge";
import { CheckCircle2, XCircle, Clock, Loader2, FileText, Download, ShieldCheck, AlertTriangle } from "lucide-react";
import axios from "axios";

const publicApi = axios.create({ baseURL: API });

const ACTION_LABELS = {
  "expense.created": "Expense submitted",
  "approver.assigned": "Approver nominated",
  "approver.approved": "Approver approved",
  "approver.rejected": "Approver rejected",
  "expense.approved": "Expense fully approved",
  "expense.rejected": "Expense rejected",
  "reminder.sent": "Reminder sent to approver",
  "reminder.failed": "Reminder failed",
  "email.sent": "Approval email sent",
  "email.failed": "Approval email failed",
  "finance.notified": "Sent to finance / accounts",
  "finance.resent": "Resent to finance",
  "accounts_software.submitted": "Submitted to accounts software",
  "accounts_software.email_sent": "Invoice emailed to accounts software",
  "accounts_software.email_failed": "Accounts software email failed",
  "submitter.notified": "Submitter notified",
};

function dotColor(action) {
  if (action.includes("approved")) return "bg-emerald-500";
  if (action.includes("rejected") || action.includes("failed")) return "bg-rose-500";
  if (action === "expense.created") return "bg-forest-700";
  if (action.startsWith("reminder")) return "bg-amber-500";
  return "bg-ink-mute";
}

export default function PublicAudit() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    (async () => {
      try {
        const { data } = await publicApi.get(`/public/audit/${token}`);
        setData(data);
      } catch (e) {
        setError(formatApiError(e));
      } finally {
        setLoading(false);
      }
    })();
  }, [token]);

  const certUrl = `${API}/public/audit/${token}/certificate.pdf`;
  const receipts = data?.receipts && data.receipts.length
    ? data.receipts
    : (data?.receipt_filename ? [{ filename: data.receipt_filename }] : []);

  if (loading) {
    return (
      <div className="min-h-screen grid place-items-center bg-cream-50 text-ink-soft">
        <div className="flex items-center"><Loader2 className="h-4 w-4 animate-spin mr-2" /> Loading audit trail…</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen grid place-items-center bg-cream-50 px-6">
        <div className="text-center max-w-sm" data-testid="audit-error">
          <div className="mx-auto h-12 w-12 rounded-full bg-rose-50 grid place-items-center mb-4"><AlertTriangle className="h-5 w-5 text-rose-500" /></div>
          <h1 className="font-heading text-2xl text-forest-900 mb-2">Audit link unavailable</h1>
          <p className="text-sm text-ink-soft">{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-cream-50 py-10 px-4">
      <div className="max-w-2xl mx-auto animate-fade-up">
        {/* Brand */}
        <div className="flex items-center gap-2 mb-8">
          <div className="h-8 w-8 rounded-md bg-forest-700 grid place-items-center">
            <span className="font-heading text-cream-50 text-sm font-semibold">3</span>
          </div>
          <div className="leading-tight">
            <div className="font-heading text-[15px] font-semibold text-forest-800">Sign-Off</div>
            <div className="text-[10px] uppercase tracking-[0.18em] text-ink-soft">Audit trail</div>
          </div>
        </div>

        {/* Summary */}
        <div className="bg-white border border-cream-200 rounded-xl p-6 mb-5" data-testid="audit-summary">
          <div className="flex items-start justify-between gap-3 mb-4">
            <div>
              <p className="text-[11px] uppercase tracking-wider text-ink-soft mb-1">Approved expense</p>
              <h1 className="font-heading text-2xl sm:text-3xl text-forest-900">{data.payee}</h1>
            </div>
            <StatusBadge status={data.status} />
          </div>
          <div className="grid grid-cols-2 gap-4 text-sm">
            <Field label="Amount">{formatCurrency(data.amount, data.currency)}</Field>
            <Field label="Submitted by">{data.submitter}</Field>
            <Field label="Submitted">{formatDateTime(data.created_at)}</Field>
            <Field label="Finance contact">{data.finance_email || "—"}</Field>
          </div>
          <div className="mt-4">
            <Field label="Description">{data.description}</Field>
          </div>
          <div className="flex flex-wrap gap-2 mt-6">
            <Button asChild className="bg-forest-700 hover:bg-forest-800 text-cream-50" data-testid="download-certificate-button">
              <a href={certUrl} target="_blank" rel="noreferrer"><Download className="h-4 w-4 mr-1.5" /> Download audit certificate (PDF)</a>
            </Button>
            {receipts.map((r, i) => (
              <Button key={r.filename || i} asChild variant="outline" className="border-cream-200 text-forest-800 hover:bg-cream-100" data-testid={`view-receipt-button-${i}`}>
                <a href={`${API}/public/audit/${token}/receipt/${i}`} target="_blank" rel="noreferrer">
                  <FileText className="h-4 w-4 mr-1.5" /> {receipts.length > 1 ? `Receipt ${i + 1}` : "View receipt"}
                </a>
              </Button>
            ))}
          </div>
        </div>

        {/* Approvers */}
        <div className="bg-white border border-cream-200 rounded-xl p-6 mb-5" data-testid="audit-approvers">
          <h2 className="font-heading text-lg text-forest-900 mb-3 flex items-center gap-2"><ShieldCheck className="h-4 w-4 text-forest-700" /> Sign-off ({data.approvers.length} approvers)</h2>
          <ul className="divide-y divide-cream-200">
            {data.approvers.map((a, i) => (
              <li key={a.email || i} className="py-2.5 flex items-center justify-between gap-2">
                <span className="text-sm text-ink truncate">{a.email}</span>
                <span className={`text-xs font-medium inline-flex items-center gap-1 ${a.decision === "approved" ? "text-emerald-700" : a.decision === "rejected" ? "text-rose-700" : "text-ink-mute"}`}>
                  {a.decision === "approved" ? <CheckCircle2 className="h-3.5 w-3.5" /> : a.decision === "rejected" ? <XCircle className="h-3.5 w-3.5" /> : <Clock className="h-3.5 w-3.5" />}
                  {a.decision === "pending" ? "Awaiting" : `${a.decision} · ${formatDateTime(a.decided_at)}`}
                </span>
              </li>
            ))}
          </ul>
        </div>

        {/* Timeline */}
        <div className="bg-white border border-cream-200 rounded-xl p-6" data-testid="audit-timeline">
          <h2 className="font-heading text-lg text-forest-900 mb-1">Tamper-proof audit log</h2>
          <p className="text-xs text-ink-soft mb-4">Every action is recorded once and never altered.</p>
          <ol className="space-y-3">
            {data.audit.map((e, i) => (
              <li key={`${e.timestamp || ""}-${i}`} className="flex gap-3">
                <span className={`mt-1.5 h-2 w-2 rounded-full shrink-0 ${dotColor(e.action)}`} />
                <div className="min-w-0">
                  <div className="text-sm font-medium text-forest-900">{ACTION_LABELS[e.action] || e.action}</div>
                  <div className="text-[11px] text-ink-mute">
                    {formatDateTime(e.timestamp)}{e.actor_email && e.actor_email !== "system" ? ` · ${e.actor_email}` : ""}{e.ip_address && e.ip_address !== "system" && e.ip_address !== "scheduler" ? ` · ${e.ip_address}` : ""}
                  </div>
                  {e.comment && <div className="text-xs text-ink-soft italic mt-0.5">“{e.comment}”</div>}
                </div>
              </li>
            ))}
          </ol>
        </div>

        <div className="mt-6 rounded-xl border border-forest-100 bg-forest-50/60 px-5 py-4" data-testid="audit-verified">
          <div className="flex items-center gap-2 text-forest-800 mb-1.5">
            <ShieldCheck className="h-4 w-4" />
            <span className="text-xs font-semibold uppercase tracking-[0.14em]">
              {data.verification?.verified ? "Cryptographically verified" : "Verification pending"}
            </span>
          </div>
          <div className="text-[12px] text-ink-soft leading-relaxed">
            Reference <strong className="text-forest-900 font-mono" data-testid="audit-reference">{data.reference}</strong>
            {" · "}Generated {formatDateTime(data.verified_at)}
            {" · "}{data.entry_count} audit entries
          </div>
          {data.verification?.verified && (
            <div className="text-[12px] text-forest-800 mt-1.5">
              Verification code{" "}
              <strong className="font-mono" data-testid="audit-verification-code">{data.verification.verification_code}</strong>
            </div>
          )}
          <div className="text-[11px] text-ink-mute mt-1.5">
            This is an authentic, untampered record produced from Sign-Off's hash-chained, append-only audit log.{" "}
            <a href="/verify" target="_blank" rel="noreferrer" className="text-forest-700 underline" data-testid="verify-link">
              Verify this independently →
            </a>
          </div>
        </div>

        <p className="text-center text-[11px] text-ink-mute mt-6">
          Sign-Off is an approval &amp; governance tool. It does not process payments or move funds.
        </p>
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <div className="text-[11px] uppercase tracking-wider text-ink-soft mb-0.5">{label}</div>
      <div className="text-forest-900 font-medium break-words">{children}</div>
    </div>
  );
}
